// ============================================================
// CLUBHUB – Spenden: die Ansichten in der Adresse
// ============================================================
// Wie bei den Mitgliedern (`lib/mitglieder/ansicht.ts`): Filter,
// Suche und Sortierung stehen in den searchParams — jede Ansicht
// laesst sich als Lesezeichen ablegen und weitergeben. Die Zahlen
// am Kopf zaehlen mit: jede Dimension zaehlt ueber alles AUSSER
// sich selbst, damit „Sachspende 3“ die drei des gewaehlten Jahres
// meint und nicht die aller Jahre.
//
// Rein und browsertauglich — gefiltert wird auf dem Server, die
// Typen braucht auch der Filterkopf.
// ============================================================
import type { DonationKind } from "@/generated/prisma/enums";
import { readSort, sortRows, type SortColumns, type SortState } from "@/lib/sorting";
import { jahrVon } from "./format";
import type { AufrufPhase, AufrufView, Belegform, BestaetigungView, SpendeView, SpenderView } from "./views";

type Frage = Record<string, string | string[] | undefined>;

const eins = (p: Frage, name: string) => {
  const w = p[name];
  return ((Array.isArray(w) ? w[0] : w) ?? "").trim();
};

const klein = (s: string | null | undefined) => (s ?? "").toLocaleLowerCase("de");

// ── Liste der Spenden ────────────────────────────────────────

export const ARTEN = [
  { schluessel: "geld", kind: "MONEY", label: "Geld", hinweis: "Geldspenden" },
  { schluessel: "sach", kind: "IN_KIND", label: "Sachspende", hinweis: "Sachspenden mit ihrem Wert" },
  { schluessel: "verzicht", kind: "WAIVER", label: "Aufwandsverzicht", hinweis: "Verzicht auf Erstattung von Aufwendungen" },
] as const satisfies readonly { schluessel: string; kind: DonationKind; label: string; hinweis: string }[];
export type ArtSchluessel = (typeof ARTEN)[number]["schluessel"] | "";

export const SPENDEN_SORT = {
  datum: { label: "Datum", first: "ab" },
  spender: { label: "Spender", first: "auf" },
  art: { label: "Art", first: "auf" },
  zweck: { label: "Zweck", first: "auf" },
  betrag: { label: "Betrag", first: "ab" },
} as const satisfies SortColumns<string>;
export type SpendenSortKey = keyof typeof SPENDEN_SORT;
export const SPENDEN_SORT_VORGABE: SpendenSortKey = "datum";

export type SpendenAnsicht = {
  q: string;
  /** Leer = laufendes Jahr, „alle“ = alle Jahre, sonst die Jahreszahl. */
  jahr: string;
  art: ArtSchluessel;
  /** ID eines Aufrufs oder „ohne“. */
  aufruf: string;
  /** „ohne“ = nur Spenden ohne Bestätigung. */
  bestaetigung: "" | "ohne";
  sort: SortState<SpendenSortKey>;
};

export const SPENDEN_PARAMS = ["q", "jahr", "art", "aufruf", "bestaetigung", "sortierung", "richtung", "umfang"] as const;

export function spendenAnsicht(p: Frage): SpendenAnsicht {
  const art = eins(p, "art");
  const jahr = eins(p, "jahr");
  return {
    q: eins(p, "q"),
    jahr: jahr === "alle" || /^\d{4}$/.test(jahr) ? jahr : "",
    art: (ARTEN.some((a) => a.schluessel === art) ? art : "") as ArtSchluessel,
    aufruf: eins(p, "aufruf"),
    bestaetigung: eins(p, "bestaetigung") === "ohne" ? "ohne" : "",
    sort: readSort(p, SPENDEN_SORT, SPENDEN_SORT_VORGABE),
  };
}

export function spendenFilterAnzahl(a: SpendenAnsicht): number {
  return [a.q, a.jahr, a.art, a.aufruf, a.bestaetigung].filter(Boolean).length;
}

/** Was die Suche durchsieht. Der hinterlegte Name anonymer Spenden NICHT — sonst verriete die Suche ihn. */
function spendenText(s: SpendeView): string {
  return klein(
    [s.spenderName, s.purpose, s.campaignTitle, s.note, s.receiptNumber, s.sach?.bezeichnung, s.verzicht?.anspruch, String(s.amount).replace(".", ",")]
      .filter(Boolean)
      .join(" "),
  );
}

type Dimension = "jahr" | "art" | "aufruf" | "bestaetigung" | "q";

function passt(s: SpendeView, a: SpendenAnsicht, laufendesJahr: number, ausser?: Dimension): boolean {
  if (ausser !== "jahr" && a.jahr !== "alle" && jahrVon(s.date) !== (a.jahr ? Number(a.jahr) : laufendesJahr)) return false;
  if (ausser !== "art" && a.art && ARTEN.find((x) => x.schluessel === a.art)?.kind !== s.kind) return false;
  if (ausser !== "aufruf" && a.aufruf && (a.aufruf === "ohne" ? s.campaignId !== null : s.campaignId !== a.aufruf)) return false;
  if (ausser !== "bestaetigung" && a.bestaetigung === "ohne" && (s.receiptId || !s.contactId)) return false;
  if (ausser !== "q" && a.q && !klein(a.q).split(/\s+/).every((w) => spendenText(s).includes(w))) return false;
  return true;
}

const SPENDEN_ORDNUNG = {
  datum: (s: SpendeView) => s.date,
  spender: (s: SpendeView) => s.spenderName ?? "",
  art: (s: SpendeView) => s.kind,
  zweck: (s: SpendeView) => s.purpose ?? s.campaignTitle ?? "",
  betrag: (s: SpendeView) => s.amount,
};

export type SpendenAuswertung = {
  passend: SpendeView[];
  jahre: { jahr: string; anzahl: number }[];
  arten: Record<string, number>;
  aufrufe: Record<string, number>;
  ohneBestaetigung: number;
};

export function spendenAuswerten(alle: SpendeView[], a: SpendenAnsicht, laufendesJahr: number): SpendenAuswertung {
  const passend = sortRows(
    alle.filter((s) => passt(s, a, laufendesJahr)),
    a.sort,
    SPENDEN_ORDNUNG,
    (x, y) => y.date.localeCompare(x.date) || y.id.localeCompare(x.id),
  );

  const fuerJahr = alle.filter((s) => passt(s, a, laufendesJahr, "jahr"));
  const jahreszahlen = [...new Set([laufendesJahr, ...alle.map((s) => jahrVon(s.date))])].sort((x, y) => y - x);
  const jahre = [
    ...jahreszahlen.map((j) => ({ jahr: String(j), anzahl: fuerJahr.filter((s) => jahrVon(s.date) === j).length })),
    { jahr: "alle", anzahl: fuerJahr.length },
  ];

  const arten: Record<string, number> = {};
  for (const s of alle.filter((x) => passt(x, a, laufendesJahr, "art"))) {
    const k = ARTEN.find((x) => x.kind === s.kind)!.schluessel;
    arten[k] = (arten[k] ?? 0) + 1;
  }

  const aufrufe: Record<string, number> = {};
  for (const s of alle.filter((x) => passt(x, a, laufendesJahr, "aufruf"))) {
    const k = s.campaignId ?? "ohne";
    aufrufe[k] = (aufrufe[k] ?? 0) + 1;
  }

  const ohneBestaetigung = alle.filter((s) => passt(s, a, laufendesJahr, "bestaetigung") && s.contactId && !s.receiptId).length;

  return { passend, jahre, arten, aufrufe, ohneBestaetigung };
}

// ── Liste der Bestätigungen ──────────────────────────────────

export const BELEG_SORT = {
  nummer: { label: "Nummer", first: "ab" },
  empfaenger: { label: "Empfänger", first: "auf" },
  zeitraum: { label: "Zeitraum", first: "ab" },
  betrag: { label: "Betrag", first: "ab" },
  ausgestellt: { label: "Ausgestellt", first: "ab" },
} as const satisfies SortColumns<string>;
export type BelegSortKey = keyof typeof BELEG_SORT;

export const BELEG_FORMEN = [
  { schluessel: "geld", form: "GELD", label: "Geld" },
  { schluessel: "sach", form: "SACH", label: "Sach" },
  { schluessel: "sammel", form: "SAMMEL", label: "Sammel" },
] as const satisfies readonly { schluessel: string; form: Belegform; label: string }[];

export const BELEG_ZUSTAENDE = [
  { schluessel: "", label: "Gültig", hinweis: "Alle nicht widerrufenen Bestätigungen" },
  { schluessel: "unversendet", label: "Nicht versendet", hinweis: "Ausgestellt, aber noch nicht verschickt" },
  { schluessel: "widerrufen", label: "Widerrufen", hinweis: "Zurückgezogene Bestätigungen" },
  { schluessel: "alle", label: "Alle", hinweis: "Auch widerrufene" },
] as const;

export type BelegAnsicht = {
  q: string;
  jahr: string;
  form: string;
  zustand: string;
  sort: SortState<BelegSortKey>;
};

export const BELEG_PARAMS = ["q", "jahr", "form", "zustand", "sortierung", "richtung", "umfang"] as const;

export function belegAnsicht(p: Frage): BelegAnsicht {
  const form = eins(p, "form");
  const zustand = eins(p, "zustand");
  const jahr = eins(p, "jahr");
  return {
    q: eins(p, "q"),
    jahr: /^\d{4}$/.test(jahr) ? jahr : "",
    form: BELEG_FORMEN.some((f) => f.schluessel === form) ? form : "",
    zustand: BELEG_ZUSTAENDE.some((z) => z.schluessel === zustand) ? zustand : "",
    sort: readSort(p, BELEG_SORT, "nummer"),
  };
}

export function belegFilterAnzahl(a: BelegAnsicht): number {
  return [a.q, a.jahr, a.form, a.zustand].filter(Boolean).length;
}

function belegPasst(b: BestaetigungView, a: BelegAnsicht, ausser?: "jahr" | "form" | "zustand"): boolean {
  if (ausser !== "zustand") {
    if (a.zustand === "" && b.cancelledAt) return false;
    if (a.zustand === "unversendet" && (b.cancelledAt || b.sentAt)) return false;
    if (a.zustand === "widerrufen" && !b.cancelledAt) return false;
  }
  if (ausser !== "jahr" && a.jahr && jahrVon(b.periodFrom) !== Number(a.jahr)) return false;
  if (ausser !== "form" && a.form && BELEG_FORMEN.find((f) => f.schluessel === a.form)?.form !== b.form) return false;
  if (a.q) {
    const t = klein(`${b.number} ${b.empfaenger.name} ${b.empfaenger.zeilen.join(" ")}`);
    if (!klein(a.q).split(/\s+/).every((w) => t.includes(w))) return false;
  }
  return true;
}

export function belegeAuswerten(alle: BestaetigungView[], a: BelegAnsicht) {
  const passend = sortRows(
    alle.filter((b) => belegPasst(b, a)),
    a.sort,
    {
      nummer: (b) => b.number,
      empfaenger: (b) => b.empfaenger.name,
      zeitraum: (b) => b.periodFrom,
      betrag: (b) => b.totalAmount,
      ausgestellt: (b) => b.issuedAt,
    },
    (x, y) => y.number.localeCompare(x.number),
  );
  const fuerJahr = alle.filter((b) => belegPasst(b, a, "jahr"));
  const jahre = [...new Set(alle.map((b) => jahrVon(b.periodFrom)))].sort((x, y) => y - x).map((j) => ({ jahr: String(j), anzahl: fuerJahr.filter((b) => jahrVon(b.periodFrom) === j).length }));
  const formen: Record<string, number> = {};
  for (const b of alle.filter((x) => belegPasst(x, a, "form"))) {
    const k = BELEG_FORMEN.find((f) => f.form === b.form)!.schluessel;
    formen[k] = (formen[k] ?? 0) + 1;
  }
  const zustaende: Record<string, number> = {};
  const basis = alle.filter((x) => belegPasst(x, a, "zustand"));
  zustaende[""] = basis.filter((b) => !b.cancelledAt).length;
  zustaende.unversendet = basis.filter((b) => !b.cancelledAt && !b.sentAt).length;
  zustaende.widerrufen = basis.filter((b) => b.cancelledAt).length;
  zustaende.alle = basis.length;
  return { passend, jahre, formen, zustaende };
}

// ── Aufrufe ──────────────────────────────────────────────────

export const PHASEN: { schluessel: string; phase: AufrufPhase | null; label: string }[] = [
  { schluessel: "", phase: null, label: "Aktuell" },
  { schluessel: "beendet", phase: "BEENDET", label: "Beendet" },
  { schluessel: "alle", phase: null, label: "Alle" },
];

export function aufrufeFiltern(alle: AufrufView[], p: Frage) {
  const phase = eins(p, "phase");
  const q = klein(eins(p, "q"));
  const gesucht = alle.filter((a) => !q || klein(`${a.title} ${a.purpose ?? ""} ${a.description ?? ""}`).includes(q));
  const zaehler: Record<string, number> = {
    "": gesucht.filter((a) => a.phase !== "BEENDET").length,
    beendet: gesucht.filter((a) => a.phase === "BEENDET").length,
    alle: gesucht.length,
  };
  const passend = gesucht
    .filter((a) => (phase === "alle" ? true : phase === "beendet" ? a.phase === "BEENDET" : a.phase !== "BEENDET"))
    .sort((a, b) => {
      const rang = { LAUFEND: 0, GEPLANT: 1, BEENDET: 2 } as const;
      return rang[a.phase] - rang[b.phase] || (b.endsAt ?? "9999").localeCompare(a.endsAt ?? "9999");
    });
  return { passend, zaehler, phase: phase === "beendet" || phase === "alle" ? phase : "", q: eins(p, "q") };
}

// ── Spender ──────────────────────────────────────────────────

export const SPENDER_SORT = {
  name: { label: "Name", first: "auf" },
  jahr: { label: "Laufendes Jahr", first: "ab" },
  vorjahr: { label: "Vorjahr", first: "ab" },
  summe: { label: "Gesamt", first: "ab" },
  letzte: { label: "Letzte Spende", first: "ab" },
} as const satisfies SortColumns<string>;
export type SpenderSortKey = keyof typeof SPENDER_SORT;
export const SPENDER_PARAMS = ["q", "offen", "art", "sortierung", "richtung", "umfang"] as const;

export function spenderAuswerten(alle: SpenderView[], p: Frage) {
  const q = klein(eins(p, "q"));
  const offen = eins(p, "offen") === "1";
  const art = eins(p, "art");
  const sort = readSort<SpenderSortKey>(p, SPENDER_SORT, "jahr");
  const basis = alle.filter((s) => !q || klein(`${s.name} ${s.memberNumber ?? ""} ${s.anschrift.zeilen.join(" ")} ${s.email ?? ""}`).includes(q));
  const nachArt = (s: SpenderView) => (art === "firmen" ? s.kind === "ORGANIZATION" : art === "personen" ? s.kind === "PERSON" : true);
  const passend = sortRows(
    basis.filter((s) => (!offen || s.offen > 0) && nachArt(s)),
    sort,
    { name: (s) => s.name, jahr: (s) => s.summeJahr, vorjahr: (s) => s.summeVorjahr, summe: (s) => s.summe, letzte: (s) => s.letzte },
    (x, y) => x.name.localeCompare(y.name, "de"),
  );
  return {
    passend,
    sort,
    q: eins(p, "q"),
    offen,
    art: art === "firmen" || art === "personen" ? art : "",
    zaehler: {
      offen: basis.filter((s) => s.offen > 0 && nachArt(s)).length,
      personen: basis.filter((s) => s.kind === "PERSON" && (!offen || s.offen > 0)).length,
      firmen: basis.filter((s) => s.kind === "ORGANIZATION" && (!offen || s.offen > 0)).length,
    },
  };
}
